// calculate monthly income from pay_rate, pay_schedule, pay_units
import React, { useState, useEffect } from 'react';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { FaLock, FaLockOpen } from 'react-icons/fa';

import breakdown from '../../json/expense_breakdown.json';
import housing from '../../json/housing_expenses.json';
import health from '../../json/out_of_pocket_health.json';
import transportData from '../../json/regional_transportation_expenses.json';
import { sortDebtorsPrimaryFirst } from '../../utils/debtors';


function calcMonthlyIncome(payRate, paySchedule, payUnits) {
  const rate = Number(payRate) || 0;
  const units = Number(payUnits) || 0;
  let annual = 0;
  switch (paySchedule) {
    case 'hourly': annual = rate * units * 52; break;
    case 'weekly': annual = rate * units * 52; break;
    case 'biweekly': annual = rate * units * 26; break;
    case 'semimonthly': annual = rate * units * 24; break;
    case 'monthly': annual = rate * units * 12; break;
    case 'yearly': annual = rate; break;
    default: annual = rate * units * 12; break;
  }
  return annual / 12;
}

// compute total net monthly income for a debtor object
function computeNetMonthly(d) {
  // employment income (net of monthly deductions)
  const employmentNet = (d.employments || []).reduce((sum, emp) => {
    const monthly = calcMonthlyIncome(emp.pay_rate, emp.pay_schedule, emp.pay_units);
    const deductions = (emp.deductions || []).reduce((s, det) => s + Number(det.amount || 0), 0);
    return sum + (monthly - deductions);
  }, 0);

  // fixed income sources
  const fixed = Number(d.interest_dividend_income || 0)
    + Number(d.family_support_payments || 0)
    + Number(d.unemployment_income || 0)
    + Number(d.social_security_income || 0)
    + Number(d.pension_income || 0);

  // government assistance
  const assist = (d.gov_assistances || []).reduce((s, ga) => s + Number(ga.amount || 0), 0);

  // other income items
  const other = (d.other_income_items || []).reduce((s, oi) => s + Number(oi.amount || 0), 0);

  // expense contributions
  const contrib = (d.expense_contributions || []).reduce((s, ec) => s + Number(ec.amount || 0), 0);

  // business incomes (net of business expenses)
  const businessNet = (d.business_incomes || []).reduce((s, bi) => {
    const biExpenses = (bi.expenses || []).reduce((se, e) => se + Number(e.amount || 0), 0);
    return s + (Number(bi.gross_revenue || 0) - biExpenses);
  }, 0);

  return employmentNet + fixed + assist + other + contrib + businessNet;
}

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona',
  AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska',
  NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  PR: 'Puerto Rico', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia'
};
// Helper to lookup housing expenses based on state, county, and household size
function getHousingExpenses(stateAbbrev, countyName, size) {
  const entries = housing[stateAbbrev] || [];
  // loose match county
  console.log(countyName)
  const match = entries.find(e =>
    e.county.toLowerCase().includes(countyName.toLowerCase()) ||
    countyName.toLowerCase().includes(e.county.toLowerCase())
  ) || entries[0] || { expenses: {} };
  const key = String(size > 5 ? 5 : size);
  const data = match.expenses[key] || {};
  return {
    mortgage: data.mortgage || 0,
    non_mortgage: data.non_mortgage || 0
  };
}
// Compute age in years from a DOB string (YYYY-MM-DD)
function getAge(dobStr) {
  const dob = new Date(dobStr);
  const diffMs = Date.now() - dob.getTime();
  const ageDt = new Date(diffMs);
  return Math.abs(ageDt.getUTCFullYear() - 1970);
}

/**
 * Determine transportation expense based on state abbreviation, county, and number of vehicles.
 * Converts state abbreviation to full state name before region lookup.
 */
function getTransportationExpense(stateAbbrev, countyName, vehicleCount) {
  // Convert abbreviation to full name
  const stateFull = STATE_NAMES[stateAbbrev] || stateAbbrev;
  // Find the region for this state
  const regionCode = Object.keys(transportData).find(rc =>
    transportData[rc].states.includes(stateFull)
  );
  if (!regionCode) return 0;
  const region = transportData[regionCode];
  // Zero vehicles => public transportation
  if (vehicleCount === 0) {
    return region.public_transportation || 0;
  }
  // Determine car key: 1 car => one_car; 2+ cars => two_cars
  const carKey = vehicleCount >= 2 ? 'two_cars' : 'one_car';
  // Check MSAs
  const msaEntries = region.operating_costs.msa || {};
  const msaMatch = Object.values(msaEntries).find(msa =>
    Array.isArray(msa.counties) && msa.counties.some(c =>
      c.toLowerCase().includes(countyName.toLowerCase()) ||
      countyName.toLowerCase().includes(c.toLowerCase())
    )
  );
  if (msaMatch && msaMatch[carKey] != null) {
    return msaMatch[carKey];
  }
  // Fallback to regional average
  return (region.operating_costs.region && region.operating_costs.region[carKey]) || 0;
}

const API = process.env.REACT_APP_API_URL;
const TOKEN = localStorage.getItem("accessToken");

// ExpenseSubsection component for handling expenses/dependents/installments for one person/household
function ExpenseSubsection({
  title,
  expenses,
  setExpenses,
  dependents,
  installments,
  setDependents,
  setInstallments,
  j2,
  d2Separate,
  caseId, API, TOKEN, caseData, netIncome,
  householdSize,
  onAfterSave,
  onDependentsChange,
  onInstallmentsChange,
}) {
  const orderedDebtors = sortDebtorsPrimaryFirst(caseData?.debtors);
  const [newRelationship, setNewRelationship] = useState("");
  const [newAge, setNewAge] = useState("");
  const [newLivesWith, setNewLivesWith] = useState(false);
  const [newRelationshipType, setNewRelationshipType] = useState('');
  // Installment payment add row state
  const [newInstallDescription, setNewInstallDescription] = useState("");
  const [newInstallAmount, setNewInstallAmount] = useState("");
  const [newInstallDate, setNewInstallDate] = useState("");
  // Budget target state
  const [budgetTarget, setBudgetTarget] = useState('');
  // Saving expenses state
  const [savingExpenses, setSavingExpenses] = useState(false);
  const categoryMap = {
    food_housekeeping: ["Food", "Housekeeping supplies"],
    clothing: ["Apparel & services"],
    personal_care: ["Personal care products & services"],
    utilities_other: ["Miscellaneous"],
  };
  // compute totals
  const totalExpenses = (expenses || []).reduce(
    (sum, exp) => sum + (Number(exp.amount) || 0),
    0
  );
  const totalInstallments = (installments || []).reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0
  );
  const grandTotal = totalExpenses + totalInstallments;
  const disposableIncome = (Number(netIncome) || 0) - grandTotal;
  const allLocked = (expenses || []).every(e => e.locked);
  // Handlers for CRUD
  const saveDependent = async (e) => {
    e.preventDefault();
    const body = {
      relationship: newRelationshipType === 'other' ? newRelationship : newRelationshipType,
      age: parseInt(newAge, 10),
      lives_with: newLivesWith,
      j2: !!j2
    };
    const res = await fetch(`${API}/cases/${caseId}/dependents/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const d = await res.json();
      setDependents(prev => [...prev, d]);
      const updated = [...(dependents || []), d];
      if (typeof onDependentsChange === 'function') onDependentsChange(updated);
      setNewRelationship("");
      setNewAge("");
      setNewLivesWith(false);
    }
  };
  const updateDependent = async (id, data) => {
    const res = await fetch(`${API}/cases/${caseId}/dependents/${id}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
      body: JSON.stringify({ ...data, j2: !!j2 }),
    });
    if (res.ok) {
      const updated = await res.json();
      setDependents(prev => prev.map(d => d.id === id ? updated : d));
      const updatedAll = (dependents || []).map(x => x.id === id ? updated : x);
      if (typeof onDependentsChange === 'function') onDependentsChange(updatedAll);
    }
  };
  const deleteDependent = async (id) => {
    const res = await fetch(`${API}/cases/${caseId}/dependents/${id}/`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${TOKEN}` },
    });
    if (res.ok) {
      setDependents(prev => prev.filter(d => d.id !== id));
      const updatedAll = (dependents || []).filter(d => d.id !== id);
      if (typeof onDependentsChange === 'function') onDependentsChange(updatedAll);
    }
  };
  // Installment Payments CRUD handlers
  const saveInstallment = async (data) => {
    const res = await fetch(`${API}/cases/${caseId}/installment-payments/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
      body: JSON.stringify({ ...data, j2: !!j2 }),
    });
    if (res.ok) {
      const newItem = await res.json();
      setInstallments(prev => [...prev, newItem]);
      const updatedAll = [...(installments || []), newItem];
      if (typeof onInstallmentsChange === 'function') onInstallmentsChange(updatedAll);
    }
  };
  const updateInstallment = async (id, data) => {
    const res = await fetch(`${API}/cases/${caseId}/installment-payments/${id}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
      body: JSON.stringify({ ...data, j2: !!j2 }),
    });
    if (res.ok) {
      const updated = await res.json();
      setInstallments(prev => prev.map(p => p.id === id ? updated : p));
      const updatedAll = (installments || []).map(x => x.id === id ? updated : x);
      if (typeof onInstallmentsChange === 'function') onInstallmentsChange(updatedAll);
    }
  };
  const deleteInstallment = async (id) => {
    const res = await fetch(`${API}/cases/${caseId}/installment-payments/${id}/`, {
      method: "DELETE", headers: { Authorization: `Bearer ${TOKEN}` },
    });
    if (res.ok) {
      setInstallments(prev => prev.filter(p => p.id !== id));
      const updatedAll = (installments || []).filter(x => x.id !== id);
      if (typeof onInstallmentsChange === 'function') onInstallmentsChange(updatedAll);
    }
  };
  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium mb-2">{title}</h3>
      {/* Dependents Section */}
      <form onSubmit={saveDependent} className="mb-4 flex items-end space-x-2">
        <select
          value={newRelationshipType}
          onChange={e => setNewRelationshipType(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Select Relationship</option>
          <option value="son">Son</option>
          <option value="daughter">Daughter</option>
          <option value="parent">Parent</option>
          <option value="grandparent">Grandparent</option>
          <option value="grandchild">Grandchild</option>
          <option value="other">Other</option>
        </select>
        {newRelationshipType === 'other' && (
          <input
            type="text"
            placeholder="Specify Relationship"
            value={newRelationship}
            onChange={e => setNewRelationship(e.target.value)}
            className="border p-2 rounded"
          />
        )}
        <input
          type="number"
          placeholder="Age"
          value={newAge}
          onChange={e => setNewAge(e.target.value)}
          className="border p-2 rounded w-20"
        />
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            checked={newLivesWith}
            onChange={() => setNewLivesWith(prev => !prev)}
          />
          <span>Lives With</span>
        </label>
        <button type="submit" className="bg-green-600 text-white px-3 py-1 rounded">Add</button>
      </form>
      <ul className="mb-6 space-y-2">
        {dependents && dependents.map(d => (
          <li key={d.id} className="flex items-center justify-between border p-2 rounded">
            <span>
              {d.relationship} (Age {d.age}) — Lives With: {d.lives_with ? "Yes" : "No"}
            </span>
            <div className="space-x-2">
              <button
                onClick={() => {
                  const rel = prompt("Relationship:", d.relationship);
                  const age = prompt("Age:", d.age);
                  // eslint-disable-next-line no-restricted-globals
                  const lw = window.confirm("Does this dependent live with the debtor?");
                  if (rel !== null && age !== null) {
                    updateDependent(d.id, { relationship: rel, age: parseInt(age,10), lives_with: lw });
                  }
                }}
                className="text-blue-600"
              >Edit</button>
              <button onClick={() => deleteDependent(d.id)} className="text-red-600">Delete</button>
            </div>
          </li>
        ))}
      </ul>
      {savingExpenses && (
        <div className="mb-4 flex items-center text-indigo-700">
          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"></path>
          </svg>
          <span>Saving expenses…</span>
        </div>
      )}
      {/* Editable Expenses Table */}
      <table className="w-full mb-4">
        <thead>
          <tr>
            <th></th>
            <th className="text-left">Category</th>
            <th className="text-left">Amount</th>
            <th className="text-left">Description</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(expenses || []).map((exp, idx) => (
            <tr key={exp.id || idx} className="border-t">
              <td
                className="text-center cursor-pointer"
                onClick={() => {
                  const newExps = [...expenses];
                  newExps[idx].locked = !newExps[idx].locked;
                  setExpenses(newExps);
                }}
              >
                {exp.locked
                  ? <FaLock className="inline-block text-gray-700" size={20}/>
                  : <FaLockOpen className="inline-block text-gray-700" size={20}/>
                }
              </td>
              <td>
                <select
                  value={exp.category}
                  onChange={e => {
                    const newExps = [...expenses];
                    newExps[idx].category = e.target.value;
                    setExpenses(newExps);
                  }}
                  className="border p-1 rounded"
                >
                  {[
                    'rent_mortgage','real_estate_taxes','home_insurance','home_maintenance',
                    'hoa_dues','second_mortgage','utilities_energy','utilities_water',
                    'utilities_phone','utilities_other','food_housekeeping','childcare',
                    'clothing','personal_care','medical','transportation','entertainment',
                    'charitable','life_insurance','health_insurance','vehicle_insurance',
                    'other_insurance','taxes','alimony_child_support','support_other',
                    'other_mortgage','other_real_estate_taxes','other_home_insurance',
                    'other_home_maintenance','other_hoa_dues','other_expense',
                    
                  ].map(c => (
                    <option key={c} value={c}>{c.replace(/_/g,' ')}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={exp.amount || ''}
                  onChange={e => {
                    const newExps = [...expenses];
                    newExps[idx].amount = parseFloat(e.target.value) || 0;
                    newExps[idx].locked = true;
                    setExpenses(newExps);
                  }}
                  className="border p-1 rounded w-24"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={exp.description || ''}
                  onChange={e => {
                    const newExps = [...expenses];
                    newExps[idx].description = e.target.value;
                    setExpenses(newExps);
                  }}
                  className="border p-1 rounded w-full"
                />
              </td>
              <td>
                <button
                  onClick={() => {
                    const newExps = expenses.filter((_, i) => i !== idx);
                    setExpenses(newExps);
                  }}
                  className="text-red-600"
                >Delete</button>
              </td>
            </tr>
          ))}
          {/* Row for adding new */}
          <tr className="border-t">
            <td></td>
            <td>
              <select
                value=""
                onChange={e => {
                  setExpenses([...(expenses || []), {
                    id: null,
                    category: e.target.value,
                    amount: 0,
                    description: '',
                    locked: false,
                    j2: !!j2
                  }]);
                }}
                className="border p-1 rounded"
              >
                <option value="">Add category...</option>
                {[
                  'rent_mortgage','real_estate_taxes','home_insurance','home_maintenance',
                    'hoa_dues','second_mortgage','utilities_energy','utilities_water',
                    'utilities_phone','utilities_other','food_housekeeping','childcare',
                    'clothing','personal_care','medical','transportation','entertainment',
                    'charitable','life_insurance','health_insurance','vehicle_insurance',
                    'other_insurance','taxes','alimony_child_support','support_other',
                    'other_mortgage','other_real_estate_taxes','other_home_insurance',
                    'other_home_maintenance','other_hoa_dues','other_expense',
                ].map(c => (
                  <option key={c} value={c}>{c.replace(/_/g,' ')}</option>
                ))}
              </select>
            </td>
            <td colSpan="3" className="text-gray-500 italic">
              Select category to add a new expense row
            </td>
          </tr>
        </tbody>
      </table>
      <div className="flex space-x-2 mb-6">
        <button
          disabled={savingExpenses}
          onClick={() =>
            setExpenses((expenses || []).map(e => ({
              ...e,
              locked: !allLocked
            })))
          }
          className={`px-4 py-2 rounded text-white bg-gray-800 ${savingExpenses ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {allLocked ? 'Unlock All' : 'Lock All'}
        </button>
        <button
          onClick={async () => {
            try {
              setSavingExpenses(true);
              const res = await fetch(`${API}/cases/${caseId}/expenses/bulk/?j2=${!!j2}`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${TOKEN}`
                },
                body: JSON.stringify(expenses)
              });
              if (res.ok) {
                const fetched = await res.json();
                setExpenses(fetched.map(e => ({ ...e, locked: false })));
                if (typeof onAfterSave === 'function') {
                  onAfterSave(fetched.map(e => ({ ...e, locked: false })));
                }
              }
            } finally {
              setSavingExpenses(false);
            }
          }}
          disabled={savingExpenses}
          className={`px-4 py-2 rounded text-white ${savingExpenses ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {savingExpenses ? (
            <span className="inline-flex items-center">
              <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"></path>
              </svg>
              Saving…
            </span>
          ) : 'Save All'}
        </button>
        <button
          disabled={savingExpenses}
          onClick={() => {
            // Only recalculate for this household (j2 flag), preserve the others
            const baseExpenses    = expenses.filter(e => e.j2 === j2);
            const otherExpenses   = expenses.filter(e => e.j2 !== j2);
            // List of all categories
            const allCategories = [
              'rent_mortgage','real_estate_taxes','home_insurance','home_maintenance',
                    'hoa_dues','second_mortgage','utilities_energy','utilities_water',
                    'utilities_phone','utilities_other','food_housekeeping','childcare',
                    'clothing','personal_care','medical','transportation','entertainment',
                    'charitable','life_insurance','health_insurance','vehicle_insurance',
                    'other_insurance','taxes','alimony_child_support','support_other',
                    'other_mortgage','other_real_estate_taxes','other_home_insurance',
                    'other_home_maintenance','other_hoa_dues','other_expense',
            ];
            // Split "Miscellaneous" into multiple categories
            const miscTotal = breakdown["Miscellaneous"]?.[householdSize] || 0;
            // Prepare allocations for miscellaneous
            const miscAllocations = [
              { category: 'food_housekeeping', pct: 0.45 },
              { category: 'clothing', pct: 0.10 },
              { category: 'personal_care', pct: 0.30 },
              { category: 'utilities_other', pct: 0.15 }
            ].map(({ category, pct }) => ({
              id: null,
              category,
              amount: Math.round(miscTotal * pct),
              description: ""
            }));
            // Calculate base expenses for primary categories
            const baseResults = Object.entries(categoryMap)
              .filter(([cat]) => cat !== 'utilities_other')
              .map(([cat, keys]) => {
                const total = keys.reduce((sum, key) => {
                const val = breakdown[key]?.[householdSize];
                  return sum + (typeof val === "number" ? val : 0);
                }, 0);
                return { id: null, category: cat, amount: Math.round(total), description: "" };
              });
            // Merge miscellaneous allocations into base results
            miscAllocations.forEach(misc => {
              const existing = baseResults.find(r => r.category === misc.category);
              if (existing) {
                existing.amount += misc.amount;
              } else {
                baseResults.push(misc);
              }
            });
            // Final calculated results
            const calcResults = baseResults;
            // housing-based rent/mortgage and utilities, split and rounded
            const { mortgage, non_mortgage } = getHousingExpenses(
              orderedDebtors[j2 ? 1 : 0]?.state,
              orderedDebtors[j2 ? 1 : 0]?.county,
              householdSize
            );
            calcResults.push({
              id: null,
              category: "rent_mortgage",
              amount: Math.round(mortgage),
              description: ""
            });
            calcResults.push({
              id: null,
              category: "utilities_energy",
              amount: Math.round(non_mortgage * 0.30),
              description: ""
            });
            calcResults.push({
              id: null,
              category: "utilities_water",
              amount: Math.round(non_mortgage * 0.15),
              description: ""
            });
            calcResults.push({
              id: null,
              category: "utilities_phone",
              amount: Math.round(non_mortgage * 0.50),
              description: ""
            });
            // Out-of-pocket medical based on age of debtors and dependents
            const ages = [];
            let dob1 = orderedDebtors[0]?.dob || "";
            if (!dob1) {
              ages.push(30);
            } else {
                ages.push(getAge(dob1));
              }
            if (orderedDebtors.length === 2){
              let dob2 = orderedDebtors[1]?.dob || dob1;
              ages.push(getAge(dob2))
            }
            // Include dependents' ages
            (dependents || []).forEach(d => ages.push(d.age));
            console.log(ages)
            // Sum out-of-pocket per person
            const medTotal = ages.reduce((sum, age) => {
              const key = age >= 65 ? '65_and_older' : 'under_65';
              return sum + (health[key] || 0);
            }, 0);
            calcResults.push({
              id: null,
              category: "medical",
              amount: Math.round(medTotal),
              description: ""
            });
            // Transportation based on number of vehicles
            const vehicleCount = Array.isArray(caseData.vehicles)
              ? (caseData.intake_data.vehicles?.length || 0)
              : 0;
            const transportAmt = getTransportationExpense(
              orderedDebtors[j2 ? 1 : 0]?.state,
              orderedDebtors[j2 ? 1 : 0]?.county,
              vehicleCount
            );
            calcResults.push({
              id: null,
              category: "transportation",
              amount: Math.round(transportAmt),
              description: ""
            });
            // Preserve locked and auto values, but include zeros for all other categories
            const calcMap = new Map(calcResults.map(e => [e.category, e]));
            const lockedExps = (baseExpenses || []).filter(e => e.locked);
            const merged = allCategories.map(cat => {
              // locked wins
              const lockedExp = lockedExps.find(e => e.category === cat);
              if (lockedExp) return lockedExp;
              // use calculated if available
              if (calcMap.has(cat)) return calcMap.get(cat);
              // otherwise default zero
              return { id: null, category: cat, amount: 0, description: "" };
            });
            setExpenses([ ...merged, ...otherExpenses ]);
          }}
          className={`px-4 py-2 rounded text-white bg-gray-600 ${savingExpenses ? 'opacity-60 cursor-not-allowed' : ''}`}
        >Auto-calculate</button>
        <input
          type="number"
          placeholder="Target disposable income..."
          value={budgetTarget}
          onChange={e => setBudgetTarget(e.target.value)}
          className="border p-1 rounded w-32 mr-2"
        />
        <button
          disabled={savingExpenses}
          onClick={() => {
            // Use only the current subsection’s expenses
            const thisHouse = expenses;
            const otherHouse = [];

            // Compute target disposable (including installments)
            const targetDisposable = Number(budgetTarget) + Number(totalInstallments);
            if (isNaN(targetDisposable)) return;

            // Sum locked expenses for this household
            const lockedSum = thisHouse
              .filter(e => e.locked)
              .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

            // Gather auto (unlocked) expenses
            const autoList = thisHouse.filter(e => !e.locked);
            const autoSum = autoList.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

            // Desired total expenses = netIncome - targetDisposable
            const expenseTarget = (Number(netIncome) || 0) - targetDisposable;

            // Available for auto expenses after locked ones
            const available = expenseTarget - lockedSum;

            // Scaling factor for auto-expenses
            const factor = autoSum > 0 && available < autoSum
              ? available / autoSum
              : 1;

            // Rebuild just-this-household list, preserving j2 flag
            const newThisHouse = thisHouse.map(e => {
              if (!e.locked) {
                const orig = Number(e.amount) || 0;
                const adjusted = Math.round(orig * factor);
                return { ...e, amount: adjusted, j2: !!j2 };
              }
              return { ...e, j2: !!j2 };
            });

            // Re-merge full list and update
            setExpenses([ ...newThisHouse, ...otherHouse ]);
          }}
          className={`px-4 py-2 rounded text-white bg-yellow-600 ${savingExpenses ? 'opacity-60 cursor-not-allowed' : ''}`}
        >Auto-Budget</button>
      </div>
      {/* Installment Payments */}
      <h3 className="text-lg font-medium mb-2">Installment Payments</h3>
      <table className="w-full mb-4">
        <thead>
          <tr>
            <th className="text-left">Description</th>
            <th className="text-left">Amount</th>
            <th className="text-left">Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(installments || []).map((p, idx) => (
            <tr key={p.id} className="border-t">
              <td>
                <input
                  type="text"
                  value={p.description}
                  onChange={e => {
                    const newList = [...installments];
                    newList[idx].description = e.target.value;
                    setInstallments(newList);
                  }}
                  className="border p-1 rounded w-full"
                />
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={p.amount}
                  onChange={e => {
                    const newList = [...installments];
                    newList[idx].amount = parseFloat(e.target.value) || 0;
                    setInstallments(newList);
                  }}
                  className="border p-1 rounded w-24"
                />
              </td>
              <td>
                <input
                  type="date"
                  value={p.payment_date}
                  onChange={e => {
                    const newList = [...installments];
                    newList[idx].payment_date = e.target.value;
                    setInstallments(newList);
                  }}
                  className="border p-1 rounded"
                />
              </td>
              <td>
                <button
                  onClick={() => updateInstallment(p.id, {
                    description: p.description,
                    amount: p.amount,
                    payment_date: p.payment_date,
                  })}
                  className="text-blue-600 mr-2"
                >Save</button>
                <button
                  onClick={() => deleteInstallment(p.id)}
                  className="text-red-600"
                >Delete</button>
              </td>
            </tr>
          ))}
          {/* Add new installment row */}
          <tr className="border-t">
            <td>
              <input
                type="text"
                placeholder="Description"
                value={newInstallDescription || ''}
                onChange={e => setNewInstallDescription(e.target.value)}
                className="border p-1 rounded w-full"
              />
            </td>
            <td>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newInstallAmount || ''}
                onChange={e => setNewInstallAmount(e.target.value)}
                className="border p-1 rounded w-24"
              />
            </td>
            <td>
              <input
                type="date"
                value={newInstallDate || ''}
                onChange={e => setNewInstallDate(e.target.value)}
                className="border p-1 rounded"
              />
            </td>
            <td>
              <button
                onClick={() => {
                  saveInstallment({
                    amount: parseFloat(newInstallAmount),
                    description: newInstallDescription,
                    payment_date: newInstallDate || null,
                  });
                  setNewInstallDescription(''); setNewInstallAmount(''); setNewInstallDate('');
                }}
                className="bg-green-600 text-white px-2 py-1 rounded"
                disabled={!newInstallAmount}
              >Add</button>
            </td>
          </tr>
        </tbody>
      </table>
      {/* Totals */}
      <div className="border-t pt-4">
        <p className="font-semibold">Total Income: ${netIncome ? netIncome.toFixed(2) : "0.00"}</p>
        <p className="mb-1">Expenses: ${totalExpenses.toFixed(2)}</p>
        <p className="mb-1">Installment Payments: ${totalInstallments.toFixed(2)}</p>
        <p className="font-semibold">Total Expenses: ${grandTotal.toFixed(2)}</p>
        <p className="font-semibold mt-2">Disposable Income: ${disposableIncome.toFixed(2)}</p>
      </div>
    </div>
  );
}

export default function ExpenseSection({ caseId, caseData, netIncome, onExpensesChange, onDependentsChange, onInstallmentsChange }) {
  const [expenses, setExpenses] = useState([]);
  const [editableExpenses, setEditableExpenses] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [sheet, setSheet] = useState({ d2_separate_household: false, account_for_nondependents: false });
  const [debtorsCount, setDebtorsCount] = useState(1);
  // Separate state for primary/j2 when d2_separate_household
  const [primaryExpenses, setPrimaryExpenses] = useState([]);
  const [j2Expenses, setJ2Expenses] = useState([]);
  const [primaryDependents, setPrimaryDependents] = useState([]);
  const [j2Dependents, setJ2Dependents] = useState([]);
  const [primaryInstallments, setPrimaryInstallments] = useState([]);
  const [j2Installments, setJ2Installments] = useState([]);

  // Helper to emit combined expenses to parent
  const emitCombinedExpenses = (separate, primaryList, j2List, singleList) => {
    if (typeof onExpensesChange !== 'function') return;
    if (separate) {
      const combined = [...(primaryList || []), ...(j2List || [])];
      onExpensesChange(combined);
    } else {
      onExpensesChange(singleList || []);
    }
  };

  // Helper to emit combined dependents to parent (ScheduleIJ)
  const emitCombinedDependents = (separate, primaryList, j2List, singleList) => {
    if (typeof onDependentsChange !== 'function') return;
    if (separate) {
      const combined = [...(primaryList || []), ...(j2List || [])];
      onDependentsChange(combined);
    } else {
      onDependentsChange(singleList || []);
    }
  };

  // Helper to emit combined installments to parent (ScheduleIJ)
  const emitCombinedInstallments = (separate, primaryList, j2List, singleList) => {
    if (typeof onInstallmentsChange !== 'function') return;
    if (separate) {
      const combined = [...(primaryList || []), ...(j2List || [])];
      onInstallmentsChange(combined);
    } else {
      onInstallmentsChange(singleList || []);
    }
  };

  useEffect(() => {
    if (caseData?.debtors) {
      setDebtorsCount(caseData.debtors.length);
    }
  }, [caseData]);
  // Load all expense data
  useEffect(() => {
    async function fetchAll() {
      try {
        const [expRes, payRes, depRes, sheetRes] = await Promise.all([
          fetch(`${API}/cases/${caseId}/expenses/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
          fetch(`${API}/cases/${caseId}/installment-payments/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
          fetch(`${API}/cases/${caseId}/dependents/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
          fetch(`${API}/cases/${caseId}/expense-sheet/`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
        ]);
        let pays, deps;
        if (expRes.ok) {
          const exps = await expRes.json();
          setExpenses(exps);
          setEditableExpenses(exps.map(e => ({ ...e, locked: false })));
          // Partition for primary/j2
          setPrimaryExpenses(exps.filter(e => !e.j2).map(e => ({ ...e, locked: false })));
          setJ2Expenses(exps.filter(e => e.j2).map(e => ({ ...e, locked: false })));
        }
        if (payRes.ok) {
          pays = await payRes.json();
          setInstallments(pays);
          setPrimaryInstallments(pays.filter(p => !p.j2));
          setJ2Installments(pays.filter(p => p.j2));
        }
        if (depRes.ok) {
          deps = await depRes.json();
          setDependents(deps);
          setPrimaryDependents(deps.filter(d => !d.j2));
          setJ2Dependents(deps.filter(d => d.j2));
        }
        if (sheetRes.ok) setSheet(await sheetRes.json());
        // Emit to parent so ScheduleIJ stays in sync
        if (expRes.ok) {
          const exps = await expRes.json();
          emitCombinedExpenses(
            (await sheetRes.ok ? (await sheetRes.json()) : sheet).d2_separate_household || sheet.d2_separate_household,
            exps.filter(e => !e.j2).map(e => ({ ...e, locked: false })),
            exps.filter(e => e.j2).map(e => ({ ...e, locked: false })),
            exps
          );
        }
        // Emit combined installments and dependents to parent
        if (typeof emitCombinedInstallments === 'function') {
          emitCombinedInstallments(
            sheet.d2_separate_household,
            pays?.filter?.(p => !p.j2) || [],
            pays?.filter?.(p => p.j2) || [],
            pays || []
          );
        }
        if (typeof emitCombinedDependents === 'function') {
          emitCombinedDependents(
            sheet.d2_separate_household,
            deps?.filter?.(d => !d.j2) || [],
            deps?.filter?.(d => d.j2) || [],
            deps || []
          );
        }
      } catch (e) {
        console.error("Error loading expenses:", e);
      }
    }
    fetchAll();
    // eslint-disable-next-line
  }, [caseId]);
  // don't render until we have caseData
  if (!caseData || !caseData.debtors) {
    return null;
  }

  // derive individual net incomes for each debtor
  // compute each debtor's net monthly income from their detailed income entries
  const incomes = (caseData?.debtors || []).map(d => computeNetMonthly(d));
  const netIncomePrimary = incomes[0] || 0;
  const netIncomeJ2 = incomes[1] || 0;
  // total net income for a non-separate household
  const totalNetIncome = netIncomePrimary + netIncomeJ2;
  // common household size when not separate
  const householdSize = debtorsCount + (dependents?.length || 0);

  // Compute household sizes for each debtor
  const primaryHouseholdSize = 1 + (primaryDependents?.length || 0);
  const j2HouseholdSize = 1 + (j2Dependents?.length || 0);

  return (
    <div className="bg-white rounded-lg shadow-2xl p-6 mx-4 sm:mx-auto sm:max-w-[1200px] mt-8">
      <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">2.
        Expenses (Household of {householdSize})
      </h3>
      {/* Global flags */}
      <div className="mb-4">
        <label className="block mb-1">
          <input
            type="checkbox"
            checked={sheet.d2_separate_household}
            onChange={async () => {
              const updated = { ...sheet, d2_separate_household: !sheet.d2_separate_household };
              const res = await fetch(`${API}/cases/${caseId}/expense-sheet/`, {
                method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
                body: JSON.stringify(updated),
              });
              if (res.ok) setSheet(await res.json());
            }}
            className="mr-2"
          />
          Debtor 2 Separate Household
        </label>
        <label className="block">
          <input
            type="checkbox"
            checked={sheet.account_for_nondependents}
            onChange={async () => {
              const updated = { ...sheet, account_for_nondependents: !sheet.account_for_nondependents };
              const res = await fetch(`${API}/cases/${caseId}/expense-sheet/`, {
                method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
                body: JSON.stringify(updated),
              });
              if (res.ok) setSheet(await res.json());
            }}
            className="mr-2"
          />
          Account for Nondependents
        </label>
        {sheet.d2_separate_household && (
          <label className="block">
            <input
              type="checkbox"
              checked={sheet.account_for_nondependents_j2}
              onChange={async () => {
                const updated = { ...sheet, account_for_nondependents_j2: !sheet.account_for_nondependents_j2 };
                const res = await fetch(`${API}/cases/${caseId}/expense-sheet/`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN}` },
                  body: JSON.stringify(updated),
                });
                if (res.ok) setSheet(await res.json());
              }}
              className="mr-2"
            />
            Account for Nondependents (Debtor 2)
          </label>
        )}
      </div>

      {sheet.d2_separate_household ? (
        <>
          <ExpenseSubsection
            title={`Expenses (Primary Household of ${primaryHouseholdSize})`}
            expenses={primaryExpenses}
            setExpenses={setPrimaryExpenses}
            dependents={primaryDependents}
            setDependents={setPrimaryDependents}
            installments={primaryInstallments}
            setInstallments={setPrimaryInstallments}
            j2={false}
            d2Separate={true}
            caseId={caseId}
            API={API}
            TOKEN={TOKEN}
            caseData={caseData}
            netIncome={netIncomePrimary}
            householdSize={primaryHouseholdSize}
            onAfterSave={(updatedList) => {
              setPrimaryExpenses(updatedList);
              // combine with current j2 list and emit
              emitCombinedExpenses(true, updatedList, j2Expenses, []);
            }}
            onDependentsChange={(list)=> emitCombinedDependents(true, list, j2Dependents, [])}
            onInstallmentsChange={(list)=> emitCombinedInstallments(true, list, j2Installments, [])}
          />
          <ExpenseSubsection
            title={`Expenses (Debtor 2 Household of ${j2HouseholdSize})`}
            expenses={j2Expenses}
            setExpenses={setJ2Expenses}
            dependents={j2Dependents}
            setDependents={setJ2Dependents}
            installments={j2Installments}
            setInstallments={setJ2Installments}
            j2={true}
            d2Separate={true}
            caseId={caseId}
            API={API}
            TOKEN={TOKEN}
            caseData={caseData}
            netIncome={netIncomeJ2}
            householdSize={j2HouseholdSize}
            onAfterSave={(updatedList) => {
              setJ2Expenses(updatedList);
              // combine with current primary list and emit
              emitCombinedExpenses(true, primaryExpenses, updatedList, []);
            }}
            onDependentsChange={(list)=> emitCombinedDependents(true, primaryDependents, list, [])}
            onInstallmentsChange={(list)=> emitCombinedInstallments(true, primaryInstallments, list, [])}
          />
        </>
      ) : (
        <ExpenseSubsection
          title={`Expenses (Household of ${householdSize})`}
          expenses={expenses}
          setExpenses={setExpenses}
          dependents={dependents}
          setDependents={setDependents}
          installments={installments}
          setInstallments={setInstallments}
          j2={false}
          d2Separate={false}
          caseId={caseId}
          API={API}
          TOKEN={TOKEN}
          caseData={caseData}
          netIncome={totalNetIncome}
          householdSize={householdSize}
          onAfterSave={(updatedList) => {
            setExpenses(updatedList);
            emitCombinedExpenses(false, [], [], updatedList);
          }}
          onDependentsChange={(list)=> emitCombinedDependents(false, [], [], list)}
          onInstallmentsChange={(list)=> emitCombinedInstallments(false, [], [], list)}
        />
      )}
    </div>
  );
}
